import { AxiosInstance } from 'axios';

/**
 * Fetching objects back out of Chargebee that the connector never received.
 *
 * Written by hand for the same reason as the trace client: `api.ts` is generated from the
 * Swagger document and anything added to it disappears on the next generation.
 *
 * The shapes mirror `Controllers/Dto/Replay`. Enums arrive as strings, because the API
 * serialises them with a string enum converter.
 */

/** The kinds of object that can be fetched back. Mirrors ReplayKind. */
export type ReplayKind =
    | 'Invoice'
    | 'CreditNote'
    | 'Customer'
    | 'Payment'
    | 'Refund';

/** What was decided about one id. Mirrors ReplayOutcome. */
export type ReplayOutcome =
    | 'Queued'
    | 'WouldQueue'
    | 'AlreadyInExact'
    | 'NotInChargebee'
    | 'Failed'
    | 'Refused';

export interface ReplayItemResult {
    id: string;
    outcome: ReplayOutcome;
    summary?: string | null;
    dependingId?: string | null;
    eventId?: string | null;
    /** What Chargebee has for this id now, in one line; null when it was not fetched. */
    chargebee?: string | null;
}

export interface ReplayResult {
    queueName: string;
    dryRun: boolean;
    requested: number;
    unique: number;
    queued: number;
    skipped: number;
    failed: number;
    results: ReplayItemResult[];
    error?: string | null;
    /**
     * What is true about the environment that makes the whole run pointless, even though
     * every id checked out. Not an error: nothing failed.
     */
    warning?: string | null;
}

export interface ReplayRequest {
    kind: ReplayKind;
    ids: string[];
    dryRun: boolean;
}

/**
 * How many ids go in one call. Must not exceed ChargebeeReplayService.MaxIdsPerRequest,
 * which refuses the whole request rather than doing part of it.
 */
export const REPLAY_CHUNK_SIZE = 250;

/**
 * Which kind of replay fetches one tracked object type back, or undefined when none does.
 *
 * The counterpart of `ObjectTraceService.ReplayKindOf`, and deliberately the smaller half: the
 * API decides whether the button exists at all - it only offers the action for a type a plan
 * covers - and this only has to name the kind for the types it already said yes to.
 */
export const REPLAY_KIND_OF: Record<string, ReplayKind> = {
    Invoice: 'Invoice',
    InvoiceSalesEntry: 'Invoice',
    CreditNote: 'CreditNote',
    CreditNoteSalesEntry: 'CreditNote',
    Customer: 'Customer',
    PaymentOnline: 'Payment',
    PaymentOnlineCredit: 'Refund',
};

/** What each kind is called on screen, and where its ids come from. */
export const REPLAY_KIND: Record<
    ReplayKind,
    { label: string; plural: string; hint: string; skipLabel: string }
> = {
    Invoice: {
        label: 'Factuur',
        plural: 'facturen',
        hint: 'Het factuurnummer zoals Chargebee het kent. Dat is hetzelfde nummer dat in een foutmelding van een betaling staat.',
        skipLabel: 'Concepten overslaan',
    },
    CreditNote: {
        label: 'Creditnota',
        plural: "creditnota's",
        hint: 'Het creditnotanummer zoals Chargebee het kent.',
        skipLabel: 'Concepten overslaan',
    },
    Customer: {
        label: 'Klant',
        plural: 'klanten',
        hint: 'Het klantnummer zoals Chargebee het kent.',
        skipLabel: 'Concepten overslaan',
    },
    Payment: {
        label: 'Betaling',
        plural: 'betalingen',
        hint: 'Het transactie-id (txn_...) zoals Chargebee het kent - niet het factuurnummer. Weet je alleen de facturen? Kies soort "Factuur" en gebruik "Betalingen bij deze facturen".',
        skipLabel: 'Alleen geslaagde betalingen',
    },
    Refund: {
        label: 'Terugbetaling',
        plural: 'terugbetalingen',
        hint: 'Het transactie-id (txn_...) van de terugbetaling zoals Chargebee het kent. De connector boekt hem op de creditnota, en alleen met SyncRefundedPayments aan.',
        skipLabel: 'Alleen geslaagde terugbetalingen',
    },
};

/**
 * What the "skip" switch leaves out per kind, for the reader who wonders what it costs.
 *
 * Two different facts under one switch, and the reasoning is the same both times: the
 * connector was never supposed to receive these, so counting them as missing would put a
 * fixed batch of false alarms at the top of every search.
 */
export const REPLAY_SKIP_HINT: Record<ReplayKind, string> = {
    Invoice: 'Een concept heeft in Chargebee nooit een invoice_generated opgeleverd, dus de connector was hem ook nooit misgelopen. Meetellen zet een vaste hoeveelheid loos alarm bovenaan de lijst.',
    CreditNote: 'Een concept heeft in Chargebee nooit een gebeurtenis opgeleverd, dus de connector was hem ook nooit misgelopen.',
    Customer: 'Een concept heeft in Chargebee nooit een gebeurtenis opgeleverd, dus de connector was hem ook nooit misgelopen.',
    Payment: 'Alleen transacties die Chargebee als geslaagde betaling kent. Een mislukte, geannuleerde of nog lopende transactie heeft nooit een payment_succeeded opgeleverd, en een terugbetaling of autorisatie is geen betaling - die aanbieden als betaling zou de connector een betaling geven die Chargebee nooit heeft gedaan.',
    Refund: 'Alleen terugbetalingen die Chargebee als geslaagd kent. Een mislukte of nog lopende terugbetaling is nooit als payment_refunded verstuurd.',
};

/**
 * What each outcome means, and whether it is bad news.
 *
 * "Al in Exact" is deliberately not an error. On a list of two hundred pasted numbers most
 * of them are usually fine, and colouring those red would bury the handful that are not.
 */
export const REPLAY_OUTCOME: Record<
    ReplayOutcome,
    { label: string; tone: 'ok' | 'muted' | 'warn' | 'error' }
> = {
    Queued: { label: 'aangeboden', tone: 'ok' },
    WouldQueue: { label: 'kan aangeboden worden', tone: 'ok' },
    AlreadyInExact: { label: 'staat al in Exact', tone: 'muted' },
    NotInChargebee: { label: 'niet in Chargebee', tone: 'warn' },
    Failed: { label: 'mislukt', tone: 'error' },
    Refused: { label: 'geweigerd', tone: 'error' },
};

/** One object in Chargebee, and whether the connector coupled it. Mirrors FindItemDto. */
export interface FindItem {
    id: string;
    status?: string | null;
    customerId?: string | null;
    date?: string | null;
    total?: number | null;
    inExact: boolean;
    dependingId?: string | null;
    trackedAs?: string | null;
    /** The invoice this hangs off, for the kinds that hang off one. Filled for payments. */
    invoiceId?: string | null;
}

/** One page of the comparison. Mirrors FindResultDto. */
export interface FindResult {
    scanned: number;
    inExact: number;
    missing: number;
    notIssued: number;
    items: FindItem[];
    nextOffset?: string | null;
    error?: string | null;
    /**
     * The ids the search could say nothing about: not in Chargebee, or there but carrying
     * none of what was asked for. Only filled by the searches that start from a list.
     */
    unanswered: string[];
}

export interface FindRequest {
    kind: ReplayKind;
    from?: Date | null;
    to?: Date | null;
    offset?: string | null;
    onlyMissing: boolean;
    skipNotIssued: boolean;
}

/**
 * How many pages the browser walks before it stops on its own.
 *
 * Chargebee pages with an opaque offset, so this is a chain rather than a set of pages that
 * can be fetched at once - and a year of invoices is long enough that somebody should be
 * asked whether to keep going. Each server page covers five hundred objects, so this is
 * fifty thousand before the question is put.
 */
export const FIND_MAX_PAGES = 100;

const parse = <T,>(data: unknown): T =>
    typeof data === 'string' ? (JSON.parse(data) as T) : (data as T);

/** A date the way the API reads one: a day, without a time that would mean nothing here. */
const day = (date: Date): string =>
    `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(
        2,
        '0'
    )}-${`${date.getDate()}`.padStart(2, '0')}`;

/** Which objects Chargebee has in a period that never reached Exact. Reads only. */
export const find = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    request: FindRequest
): Promise<FindResult> => {
    const response = await axios.post(
        `/Replay/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/find`,
        {
            kind: request.kind,
            from: request.from ? day(request.from) : null,
            to: request.to ? day(request.to) : null,
            offset: request.offset ?? null,
            onlyMissing: request.onlyMissing,
            skipNotIssued: request.skipNotIssued,
        }
    );

    return parse<FindResult>(response.data);
};

/**
 * How many invoices go in one payment lookup. Must not exceed
 * ChargebeeInventoryService.MaxInvoicesPerRequest, which refuses the whole request.
 */
export const PAYMENTS_CHUNK_SIZE = 100;

/**
 * Which payments Chargebee has against these invoices, and which of those are not in Exact.
 *
 * The step after fetching invoices back. A replayed invoice does not bring its payments
 * along - a payment is its own webhook, missed just as separately - so this asks Chargebee
 * which payments belong to them. Reads only.
 */
export const paymentsOfInvoices = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    invoiceIds: string[],
    onlySucceeded: boolean
): Promise<FindResult> => {
    const response = await axios.post(
        `/Replay/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/payments`,
        {
            invoiceIds,
            onlyMissing: true,
            onlySucceeded,
        }
    );

    return parse<FindResult>(response.data);
};

/** Splits a pasted block into ids: one per line, comma or semicolon separated too. */
export const parseIds = (text: string): string[] =>
    text
        .split(/[\s,;]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

export const replay = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    request: ReplayRequest
): Promise<ReplayResult> => {
    const response = await axios.post(
        `/Replay/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}`,
        request
    );

    return parse<ReplayResult>(response.data);
};

/** What was decided about one stored body. Mirrors ReprocessOutcome. */
export type ReprocessOutcome =
    | 'Queued'
    | 'WouldQueue'
    | 'NotStored'
    | 'Failed';

export interface ReprocessItemResult {
    eventType: string;
    eventId: string;
    /** The queue message that was written, or would be. */
    message?: string | null;
    table?: string | null;
    queueName?: string | null;
    storedAt?: string | null;
    queueMessageId?: string | null;
    outcome: ReprocessOutcome;
    summary?: string | null;
}

export interface ReprocessResult {
    dryRun: boolean;
    requested: number;
    unique: number;
    queued: number;
    missing: number;
    failed: number;
    results: ReprocessItemResult[];
    error?: string | null;
}

/**
 * Offers a raw webhook body that is still stored in an inbound table to the receive path
 * again.
 *
 * The cheapest of the three ways back, and the only one that fits an object whose webhook was
 * never processed: a requeue needs a stored copy that does not exist yet, and a replay would
 * fetch a body out of Chargebee that is already sitting in the table. This writes the queue
 * message that names the body and nothing else, so nothing is overwritten and a second attempt
 * after a success finds the body gone rather than doing it twice.
 */
export const reprocessInbound = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    items: { eventType: string; eventId: string }[],
    dryRun: boolean
): Promise<ReprocessResult> => {
    const response = await axios.post(
        `/Replay/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/reprocess`,
        { items, dryRun }
    );

    return parse<ReprocessResult>(response.data);
};
